import { useEffect } from 'react';
import { useCurrentView } from './useCurrentView';
import { pathToView, viewToPath } from '../lib/viewRouter';

/**
 * Keeps the browser URL and the current view in sync.
 *
 * - On mount: if the URL names a known view (a deep-link), that wins and is
 *   applied to the store. Otherwise, if we're at the root path, we reflect the
 *   persisted view into the URL via replaceState. Any OTHER unrecognized path
 *   (e.g. /assess/:token, handled separately in App.tsx) is left untouched.
 * - On popstate (back/forward): re-apply the view from the URL.
 *
 * Forward navigation (clicking around) is handled by setCurrentView itself,
 * which pushes the path — so this hook only deals with load + history events.
 */
export function useViewUrlSync() {
  useEffect(() => {
    const applyFromLocation = (): boolean => {
      const parsed = pathToView(window.location.pathname);
      if (!parsed) return false;
      if (parsed.view === 'domain' && parsed.domainId) {
        localStorage.setItem('selectedDomainId', parsed.domainId);
      }
      if (parsed.view === 'profile' && parsed.profileId) {
        localStorage.setItem('selectedProfileId', parsed.profileId);
        // hydrate the in-memory selection so ProfileView paints on deep-link/reload
        import('../stores/socialStore').then(({ useSocialStore }) => {
          useSocialStore.setState({ profileUserId: parsed.profileId! });
        });
      }
      if (parsed.roadmapTrack) {
        /* A retired page's URL: open the Roadmap on the track that replaced it, and
           correct the address bar so it no longer names the retired page. */
        localStorage.setItem('roadmapTrack', parsed.roadmapTrack);
        window.dispatchEvent(new CustomEvent('roadmapTrack', { detail: parsed.roadmapTrack }));
        window.history.replaceState({ view: parsed.view }, '', viewToPath(parsed.view));
      }
      useCurrentView.getState().setViewFromUrl(parsed.view);
      return true;
    };

    if (!applyFromLocation()) {
      // Only seed the URL from the persisted view when we're at the root.
      // Leave unrecognized paths (e.g. /assess/:token) alone.
      const path = window.location.pathname;
      if (path === '/' || path === '') {
        const view = useCurrentView.getState().currentView;
        const domainId = view === 'domain' ? localStorage.getItem('selectedDomainId') : null;
        window.history.replaceState({ view }, '', viewToPath(view, domainId));
      }
    }

    const onPopState = () => {
      applyFromLocation();
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);
}
